import { Button, IconButton } from "components/ui/button";
import React from "react";
import { Tooltip } from "components/ui/tooltip";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useOnCopyToClipboard } from "hooks/use-copy-to-clipboard";
import { Popper } from "components/ui/popper";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import Grow from "@mui/material/Grow";
import { Paper } from "components/ui/paper";
import { MenuItem, MenuList } from "components/ui/menu";
import { processHTMLStrToBase64 } from "utils/export-html";
import { useLoading } from "hooks/use-loading";
import { htmlToMarkdown } from "client-server-shared/utils/parser";
import { CircularProgress } from "./ui/loading";
import { useDashboardTranslation } from "./translate";
import { colors } from "src/constants";

const options = [
  {
    label: "Plain Text",
    value: "plain-text",
  },
  {
    label: "Markdown",
    value: "markdown",
  },
  {
    label: "HTML",
    value: "html",
  },
];

const CopyButtonWithOptions = ({
  content,
  getPlainText,
  sx = {},
  iconProps = {},
  onClick = () => {},
  predefinedOptions,
  showOptionsRightAway,
  afterClick,
}: {
  onClick?: () => void;
  getPlainText: () => string;
  sx?: any;
  content: string;
  iconProps?: any;
  predefinedOptions?: {
    label: string;
    value: string;
    getText: () => Promise<string> | string;
  }[];
  afterClick?: () => void;
}) => {
  const { isLoading, onLoading, onIdle } = useLoading();
  const { onCopy } = useOnCopyToClipboard();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const { t } = useDashboardTranslation();
  const handleMenuItemClick = async (option: string) => {
    setOpen(false);

    if (predefinedOptions) {
      const foundOption = predefinedOptions.find(
        (item) => item.value === option
      );
      if (foundOption) {
        onCopy(await foundOption.getText());
      }

      return;
    }

    if (option === "plain-text") {
      onCopy(getPlainText());
    } else {
      if (isLoading) {
        return;
      }
      try {
        let text = "";
        onLoading();
        text = await processHTMLStrToBase64(content, true);

        if (option === "markdown") {
          text = await htmlToMarkdown(text);
        }
        onCopy(text);
        onIdle();
      } catch (e) {
        onIdle();
      }
    }

    if (afterClick) {
      afterClick();
    }
  };

  const handleClose = (event: Event) => {
    setOpen(false);
  };

  if (showOptionsRightAway) {
    return (
      <MenuList
        sx={{
          display: "flex",
          columnGap: "8px",
          rowGap: "8px",
          flexWrap: "wrap",
          padding: 0,
        }}
      >
        {(predefinedOptions || options).map((option, index) => (
          <MenuItem
            key={option.label}
            disabled={option.disabled}
            sx={{
              padding: "6px 12px",
              fontSize: "12px",
              borderRadius: "6px",
              border: `1px solid ${colors.gray2}`,
            }}
            onClick={(event) => {
              if (option.disabled) {
                return;
              }
              handleMenuItemClick(option.value);
            }}
          >
            {t(option.label)}
          </MenuItem>
        ))}
      </MenuList>
    );
  }

  return (
    <>
      <Button
        ref={anchorRef}
        onMouseEnter={() => {
          setOpen(true);
        }}
        capitalize
        sx={{
          padding: "0 !important",
          background: "none",
          height: "initial !important",
          width: "initial !important",
          minWidth: "initial !important",
          color: "rgb(86, 99, 108)",
          display: "flex!important",
          "& .MuiButton-startIcon": {
            marginRight: "4px",
          },
          ...sx,
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!getPlainText) {
            return;
          }
          if (!onClick) {
            return;
          }
          onClick();
          let text = getPlainText();

          if (text) {
            onCopy(text);
          }
        }}
        startIcon={
          isLoading ? (
            <CircularProgress size={14} />
          ) : (
            <ContentCopyOutlinedIcon
              fontSize="small"
              sx={{
                height: "14px",
                width: "14px",
                fill: "rgba(0, 0, 0, 0.5)",
                ...(iconProps.sx || {}),
              }}
            />
          )
        }
      >
        {t("Copy")}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{
          zIndex: 3,
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  onMouseLeave={() => {
                    setOpen(false);
                  }}
                >
                  {(predefinedOptions || options).map((option, index) => (
                    <MenuItem
                      key={option.label}
                      disabled={option.disabled}
                      sx={{
                        fontSize: "12px",
                        borderRadius: "6px",
                        color: "#3E5060",
                      }}
                      onClick={(event) => {
                        if (option.disabled) {
                          return;
                        }
                        handleMenuItemClick(option.value);
                      }}
                    >
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default CopyButtonWithOptions;
